import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { parentTreeItem } from "library/tree/index.mjs"
import { PlanRoot } from "library/tree/constants.mjs"
import { Box, Button, ButtonGroup, Paper, Popover, Stack } from "@mui/material"
import { GROUP } from "library/constants.mjs"
import { Repeat } from "lib/@components/repeat"
import { RegimeButtons } from "slot-definitions"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useRef, useState } from "react"
import { navigate } from "lib/routes/navigate"
import { ChildFolder } from "routes/asset-register/plugins/regime-display/child-folder"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { OpenDialog } from "event-definitions"
import { If } from "lib/@components/switch"
import Iconify from "minimals-template/components/Iconify"
import { useTreeItemIndex } from "routes/schedule/lib/useTreeItemIndex"
import { Feature } from "lib/@components/feature"
import { FEATURE_FLAG_REGIME_NAVIGATOR } from "./feature-flag-regime-navigator.runtime"

export function RegimeNavigator({ children: contents, noButtons = true, sx }) {
    const { currentTab = "", parentItem, parent = parentItem, regimeNavigatorRoute } = useBoundContext()
    const ref = useRef()
    const [open, setOpen] = useState()
    const treeIndex = useTreeItemIndex(true)
    OpenDialog.useEvent(() => {
        setOpen(null)
    })
    if (!parent) return null
    const canGoUp = !parent[PlanRoot]
    const folders = canGoUp
        ? parent[parentTreeItem].children
              .filter((c) => c.type === GROUP)
              .map((g) => ({
                  value: g.id,
                  label: g.label,
              }))
        : [{ value: parent.id, label: parent.label }]
    const children = parent.children.filter((c) => c.type === GROUP)
    return (
        <Feature feature={FEATURE_FLAG_REGIME_NAVIGATOR}>
            {/* <Divider sx={{ mt: 1 }} /> */}
            <ListItemBox py={2} sx={sx}>
                <Stack direction="row" spacing={2}>
                    <Stack>
                        <Button
                            ref={ref}
                            startIcon={<Iconify icon="streamline:location-target-1-solid" />}
                            size="small"
                            data-cy="regime-navigator"
                            endIcon={
                                open ? <Iconify icon="mdi:arrow-drop-up" /> : <Iconify icon="mdi:arrow-down-drop" />
                            }
                            onClick={() => setOpen(ref.current)}
                        >
                            {parent.label}
                        </Button>
                    </Stack>
                    <If truthy={!noButtons}>
                        <ButtonGroup sx={{ mt: 1 }} size="small">
                            <RegimeButtons.Slot />
                        </ButtonGroup>
                    </If>
                </Stack>
                <Box flex={1} />
                {contents}
            </ListItemBox>
            <Popover
                open={Boolean(open)}
                onClose={() => setOpen(null)}
                data-cy="regime-navigator-popover"
                anchorEl={open}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Paper elevation={8}>
                    <Box sx={{ px: 1, minWidth: "25em" }}>
                        <Stack direction="row" spacing={2} sx={{ pl: 2 }} alignItems="flex-start">
                            <Button
                                size="small"
                                disabled={!canGoUp}
                                onClick={() => go(parent[parentTreeItem].id)}
                                startIcon={<Iconify icon="mdi:arrow-upward" />}
                                sx={{ mt: 1, fontWeight: 100 }}
                                data-cy="regime-navigator-popover-btn"
                            >
                                {canGoUp ? parent[parentTreeItem]?.label ?? "" : ""}
                                <Box pt={1} />
                            </Button>
                            <Box pt={0.9}>
                                <StandardSelector
                                    optionSx={{ fontSize: 13 }}
                                    options={folders}
                                    value={parent.id}
                                    size="small"
                                    onChange={go}
                                />
                                <Box pt={1} />
                            </Box>

                            <Stack sx={{ alignSelf: "flex-start", maxHeight: "11.5em", overflowY: "auto" }}>
                                <Box pt={2} />
                                <Repeat list={children} item={<ChildFolder />} />
                                <Box pt={1} />
                            </Stack>
                        </Stack>
                    </Box>
                </Paper>
            </Popover>
        </Feature>
    )

    function go(id = parent[parentTreeItem].id) {
        const item = treeIndex.retrieve(id)
        navigate(getRoute(item, id))
    }

    function getRoute(item, id) {
        if (regimeNavigatorRoute) {
            return regimeNavigatorRoute(item)
        }

        return `/app/schedules?id=${id}&${currentTab}`
    }
}
